import { Container, Sprite, Texture } from 'pixi.js';
import gsap from 'gsap';
import { TUNING, type Side } from '../config/tuning';
import { degToRad } from '../engine/math';

export type EmberState = 'idle' | 'inhale' | 'warning';

/**
 * Joint — tapered cone (filter + paper + ember).
 *
 * Layout in the local container (ROOT at the holder's pinched fingertips):
 *   player holder (hand on LEFT):   filter @ x=0 extending right,  paper extends right, ember at far right
 *   AI holder   (hand on RIGHT):   filter @ x=0 extending left,   paper extends left,  ember at far left
 *
 * Paper is a trapezoid narrow-left/wide-right in its source SVG. When the AI holds
 * it we horizontally mirror paper + filter so the "narrow" end is always toward the
 * hand. A subtle ±8° rotation makes the joint look naturally held between fingers.
 */
export class Joint extends Container {
  private filter: Sprite;
  private paper: Sprite;
  private embers: Record<EmberState, Sprite>;
  private currentEmber: EmberState = 'idle';
  private paperBaseWidth: number;
  private holder: Side = 'player';
  private currentLengthPct = 100;

  constructor(x = 0, y = 0) {
    super();
    this.position.set(x, y);
    this.sortableChildren = true;

    this.filter = new Sprite(Texture.from('joint_filter'));
    this.paper = new Sprite(Texture.from('joint_paper'));
    this.paperBaseWidth = TUNING.joint.paperVisualLengthPx;

    this.embers = {
      idle: new Sprite(Texture.from('joint_ember_idle')),
      inhale: new Sprite(Texture.from('joint_ember_inhale')),
      warning: new Sprite(Texture.from('joint_ember_warning')),
    };
    this.embers.idle.anchor.set(0.5);
    this.embers.inhale.anchor.set(0.5);
    this.embers.warning.anchor.set(0.5);
    this.embers.inhale.visible = false;
    this.embers.warning.visible = false;

    this.addChild(this.filter, this.paper, this.embers.idle, this.embers.inhale, this.embers.warning);

    this.setHolder('player');
    this.setLengthPct(100);
  }

  /** Holder=player → paper extends right. Holder=ai → extends left. */
  setHolder(holder: Side): void {
    this.holder = holder;
    // Subtle tilt so the joint looks naturally held between pinched fingers.
    this.rotation = degToRad(holder === 'player' ? -8 : 8);
    this.layoutPieces();
  }

  setLengthPct(pct: number): void {
    this.currentLengthPct = Math.max(0, Math.min(100, pct));
    this.layoutPieces();
    if (pct <= 0) {
      this.paper.visible = false;
      this.embers.idle.visible = false;
      this.embers.inhale.visible = false;
      this.embers.warning.visible = false;
    } else {
      this.paper.visible = true;
    }
  }

  setEmber(state: EmberState): void {
    if (this.currentEmber === state) return;
    this.embers[this.currentEmber].visible = false;
    this.embers[state].visible = true;
    this.currentEmber = state;
  }

  pulseEmber(): void {
    const e = this.embers[this.currentEmber];
    const baseX = e.scale.x;
    const baseY = e.scale.y;
    gsap.to(e.scale, {
      x: baseX * 1.18,
      y: baseY * 1.18,
      duration: 0.22,
      yoyo: true,
      repeat: 1,
      ease: 'sine.inOut',
    });
  }

  /** Per-state compensation for different ember SVG sizes + joint-length scale. */
  private static readonly EMBER_STATE_BASE: Record<EmberState, number> = {
    idle: 1.0,
    inhale: 0.82,
    warning: 0.78,
  };

  private layoutPieces(): void {
    const ratio = this.currentLengthPct / 100;
    const paperW = this.paperBaseWidth * ratio;
    const filterW = TUNING.joint.filterVisualLengthPx;
    const jointScale = 0.25 + 0.75 * ratio;

    if (this.holder === 'player') {
      // Hand on LEFT, paper extends right, default SVG orientation.
      this.setSpriteWidth(this.filter, filterW, false);
      this.filter.anchor.set(0, 0.5);
      this.filter.position.set(0, 0);

      this.setSpriteWidth(this.paper, paperW, false);
      this.paper.anchor.set(0, 0.5);
      this.paper.position.set(filterW, 0);

      this.applyEmberLayout(filterW + paperW, jointScale);
    } else {
      // Hand on RIGHT, paper extends left. Flip filter + paper horizontally so
      // their narrow ends sit next to the hand. anchor(0, 0.5) + negative
      // scale.x means each sprite draws LEFT of its position, so the filter
      // covers [0, -filterW] and the paper must start at x=-filterW to join it.
      this.setSpriteWidth(this.filter, filterW, true);
      this.filter.anchor.set(0, 0.5);
      this.filter.position.set(0, 0);

      this.setSpriteWidth(this.paper, paperW, true);
      this.paper.anchor.set(0, 0.5);
      this.paper.position.set(-filterW, 0);

      this.applyEmberLayout(-filterW - paperW, jointScale);
    }
  }

  /**
   * Sets a sprite's display width while preserving natural scaleY, optionally
   * horizontally flipped. Replaces Phaser's `displayWidth = N` + `setFlipX(true)`.
   */
  private setSpriteWidth(sprite: Sprite, displayW: number, flipX: boolean): void {
    const texW = sprite.texture.width;
    if (texW <= 0) return;
    const absScaleX = displayW / texW;
    sprite.scale.set(flipX ? -absScaleX : absScaleX, 1);
  }

  private applyEmberLayout(tipX: number, jointScale: number): void {
    (Object.keys(this.embers) as EmberState[]).forEach((state) => {
      const e = this.embers[state];
      e.position.set(tipX, 0);
      const s = jointScale * Joint.EMBER_STATE_BASE[state];
      e.scale.set(s);
    });
  }
}
